import { Close } from "@mui/icons-material";
import {
  Dialog,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";

function Card({ title, contents }: { title: string; contents: string[] }) {
  return (
    <Stack sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
      <Stack
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f6f5f4",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Noto Sans JP",
            fontWeight: 400,
            fontStyle: "Regular",
            fontSize: "14px",
            leadingTrim: "NONE",
            lineHeight: "185%",
            letterSpacing: "0.17px",
            textAlign: "justify",
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack sx={{ px: 2, py: 3 }}>
        <List
          sx={{
            py: 0,
            pl: 5,
            listStyleType: "disc",
            "& .MuiListItem-root": {
              display: "list-item",
              "&::marker": {
                content: "'・ '",
              },
            },
          }}
        >
          {contents.map((value, i) => (
            <ListItem disablePadding key={i}>
              <ListItemText>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Noto Sans JP",
                    fontWeight: 400,
                    fontStyle: "Regular",
                    fontSize: "14px",
                    leadingTrim: "NONE",
                    lineHeight: "185%",
                    letterSpacing: "0.17px",
                  }}
                >
                  {value}
                </Typography>
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Stack>
    </Stack>
  );
}

export interface ClinicalDepartmentDialogLinkProps {
  children: string;
}

export default function ClinicalDepartmentDialogLink({
  children,
}: ClinicalDepartmentDialogLinkProps) {
  const [open, setOpen] = useState(false);
  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);
  return (
    <>
      <Link
        onClick={handleClickOpen}
        variant="body2"
        sx={{
          cursor: "pointer",
          fontFamily: "Noto Sans JP",
          fontWeight: 400,
          fontStyle: "Regular",
          fontSize: "14px",
          leadingTrim: "NONE",
          lineHeight: "185%",
          letterSpacing: "0.17px",
          textAlign: "justify",
        }}
      >
        {children}
      </Link>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Stack sx={{ px: 4, pt: 4, pb: 12, gap: 6 }}>
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: "#3D3D3D",
                fontFamily: "Noto Sans JP",
                fontWeight: 500,
                fontStyle: "Medium",
                fontSize: "16px",
                leadingTrim: "NONE",
                lineHeight: "180%",
                letterSpacing: "0.15px",
                textAlign: "center",
              }}
            >
              受診すべき診療科について
            </Typography>
            <IconButton aria-label="close" onClick={handleClose} sx={{ p: 0 }}>
              <Close />
            </IconButton>
          </Stack>
          <Divider />
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "14px",
              leadingTrim: "NONE",
              lineHeight: "185%",
              letterSpacing: "0.17px",
              textAlign: "justify",
            }}
          >
            一般的に睡眠時無呼吸症候群・不眠症については以下の診療科でご相談いただけます。
          </Typography>
          <Stack gap={4}>
            <Card
              title="睡眠時無呼吸症候群の相談"
              contents={["内科", "耳鼻咽喉科", "呼吸器内科", "循環器内科"]}
            />
            <Card
              title="不眠症の相談"
              contents={["内科", "心療内科", "精神科"]}
            />
          </Stack>
          <Typography
            variant="body2"
            color="#3D3D3D"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "14px",
              leadingTrim: "NONE",
              lineHeight: "185%",
              letterSpacing: "0.17px",
              textAlign: "justify",
            }}
          >
            また、診療科としては標ぼうされていませんが睡眠外来や睡眠クリニックも多く存在するので、通いやすい病院を探すとよいでしょう。
          </Typography>
        </Stack>
      </Dialog>
    </>
  );
}
